"use client"

import { useEffect, useState } from "react"
import { Card, Spin } from "antd"
import { LoadingOutlined } from "@ant-design/icons"

const PivotTableForAnalytics = ({ columnsData, gridData, pivotConfig, onExportDataReady }) => {
  const [loading, setLoading] = useState(false)
  const [pivotData, setPivotData] = useState({
    headers: [],
    rows: [],
    grandTotals: {},
  })
  const [exportData, setExportData] = useState(null)

  useEffect(() => {
    if (pivotConfig && pivotConfig.enabled === "Y" && gridData.length > 0) {
      generatePivotTable()
    }
  }, [gridData, pivotConfig, columnsData])

  useEffect(() => {
    if (exportData && onExportDataReady) {
      onExportDataReady(exportData)
    }
  }, [exportData, onExportDataReady])

  const generatePivotTable = () => {
    setLoading(true)

    try {
      const { rows: rowFields, columns: columnFields, values: valueFields, formulaColumns } = pivotConfig

      console.log("Pivot Config:", pivotConfig)
      console.log("Grid Data:", gridData)
      console.log("Columns Data:", columnsData)

      // Get unique values for column fields (product categories)
      const columnValues = getUniqueColumnValues(gridData, columnFields)
      console.log("Column Values:", columnValues)

      // Get unique values for row combinations
      const rowValues = getUniqueRowValues(gridData, rowFields)
      console.log("Row Values:", rowValues)

      // Generate pivot rows
      const rows = generatePivotRows(
        gridData,
        rowValues,
        columnValues,
        rowFields,
        columnFields,
        valueFields,
        formulaColumns,
      )

      // Calculate grand totals
      const grandTotals = calculateGrandTotals(gridData, columnValues, columnFields, valueFields, pivotConfig, rows)

      const pivotTableData = {
        headers: [],
        rows,
        grandTotals,
      }

      setPivotData(pivotTableData)

      // Generate export data structure
      const exportData = generateExportData(rows, columnValues, rowFields, valueFields, grandTotals, formulaColumns)
      setExportData(exportData)
    } catch (error) {
      console.error("Error generating pivot table:", error)
    } finally {
      setLoading(false)
    }
  }

  const generateExportData = (rows, columnValues, rowFields, valueFields, grandTotals, formulaColumns) => {
    const exportRows = []

    // Create header row
    const headerRow = {}

    // Add row field headers
    rowFields.forEach((field) => {
      headerRow[field] = getDisplayName(field)
    })

    // Add column value headers with value field names and formula columns
    columnValues.forEach((columnValue) => {
      valueFields.forEach((valueField) => {
        const headerKey = `${columnValue}_${valueField}`
        headerRow[headerKey] = `${columnValue}`
      })
    })

    // Add total headers
    valueFields.forEach((valueField) => {
      headerRow[`total_${valueField}`] = `Total`
    })

    // Add formula total headers
    if (formulaColumns && formulaColumns.length > 0) {
      formulaColumns.forEach((formula) => {
        headerRow[`total_${formula.name}`] = `Total ${formula.displayName}`
      })
    }

    exportRows.push(headerRow)

    // Add data rows
    rows.forEach((row) => {
      const exportRow = {}

      // Add row field values
      rowFields.forEach((field) => {
        const rowVal = row.rowData[field]
        exportRow[field] = typeof rowVal === "string" ? rowVal.trim() : rowVal
      })

      // Add column values
      columnValues.forEach((columnValue) => {
        valueFields.forEach((valueField) => {
          const cellKey = `${columnValue}|${valueField}`
          const headerKey = `${columnValue}_${valueField}`
          exportRow[headerKey] = row.rowData[cellKey] || 0
        })
      })

      // Add row totals
      valueFields.forEach((valueField) => {
        exportRow[`total_${valueField}`] = row.totals[valueField] || 0
      })

      // Add formula row totals
      if (formulaColumns && formulaColumns.length > 0) {
        formulaColumns.forEach((formula) => {
          exportRow[`total_${formula.name}`] = row.totals[formula.name] || 0
        })
      }

      exportRows.push(exportRow)
    })

    // Add grand total row
    const grandTotalRow = {}

    // Add "Grand Total" label in first column
    rowFields.forEach((field, index) => {
      grandTotalRow[field] = index === 0 ? "Grand Total" : ""
    })

    // Add grand totals for each column
    columnValues.forEach((columnValue) => {
      valueFields.forEach((valueField) => {
        const cellKey = `${columnValue}|${valueField}`
        const headerKey = `${columnValue}_${valueField}`
        grandTotalRow[headerKey] = grandTotals[cellKey] || 0
      })
    })

    // Add overall grand totals
    valueFields.forEach((valueField) => {
      grandTotalRow[`total_${valueField}`] = grandTotals[`total_${valueField}`] || 0
    })

    // Add overall formula grand totals
    if (formulaColumns && formulaColumns.length > 0) {
      formulaColumns.forEach((formula) => {
        grandTotalRow[`total_${formula.name}`] = grandTotals[`total_${formula.name}`] || 0
      })
    }

    exportRows.push(grandTotalRow)

    return exportRows
  }

  const getUniqueColumnValues = (data, columnFields) => {
    const uniqueValues = new Set()
    data.forEach((row) => {
      columnFields.forEach((field) => {
        const value = row[field]
        if (value !== null && value !== undefined && value !== "") {
          uniqueValues.add(value)
        }
      })
    })
    return Array.from(uniqueValues).sort()
  }

  const getUniqueRowValues = (data, rowFields) => {
    const uniqueRows = new Map()

    data.forEach((row) => {
      const rowKey = rowFields.map((field) => row[field] || "").join("|")
      if (!uniqueRows.has(rowKey)) {
        const rowData = {}
        rowFields.forEach((field) => {
          rowData[field] = row[field] || ""
        })
        uniqueRows.set(rowKey, rowData)
      }
    })

    return Array.from(uniqueRows.entries()).map(([key, data]) => ({ key, data }))
  }

  const generatePivotRows = (data, rowValues, columnValues, rowFields, columnFields, valueFields, formulaColumns) => {
    const rows = []

    rowValues.forEach(({ key: rowKey, data: rowData }) => {
      const row = {
        key: rowKey,
        rowData: { ...rowData },
        totals: {},
      }

      // Calculate values for each column (product category)
      columnValues.forEach((columnValue) => {
        valueFields.forEach((valueField) => {
          const cellKey = `${columnValue}|${valueField}`

          // Filter data for this specific row and column combination
          const filteredData = data.filter((dataRow) => {
            // Check if row fields match
            const rowMatch = rowFields.every((field) => {
              const rowValue = rowData[field]
              const dataValue = dataRow[field]
              return String(rowValue || "").trim() === String(dataValue || "").trim()
            })

            // Check if column fields match
            const columnMatch = columnFields.some((field) => {
              const dataValue = dataRow[field]
              return String(dataValue || "").trim() === String(columnValue || "").trim()
            })

            return rowMatch && columnMatch
          })

          // console.log(`Row: ${rowKey}, Column: ${columnValue}, Filtered Data:`, filteredData)

          // Sum the values
          const sum = filteredData.reduce((total, dataRow) => {
            const value = Number.parseFloat(dataRow[valueField]) || 0
            return total + value
          }, 0)

          row.rowData[cellKey] = sum
        })

        // Calculate formula columns for this column value
        if (formulaColumns && formulaColumns.length > 0) {
          formulaColumns.forEach((formula) => {
            const formulaKey = `${columnValue}|${formula.name}`

            // Get the values needed for the formula calculation
            const getFieldVal = (f) => {
              if (rowFields.includes(f)) return rowData[f] || 0
              return row.rowData[`${columnValue}|${f}`] || 0
            }

            const field1Value = getFieldVal(formula.fields[0])
            const field2Value = getFieldVal(formula.fields[1])

            // Calculate based on operation
            let formulaResult = 0
            switch (formula.operation) {
              case "divide":
                formulaResult = field2Value !== 0 ? field1Value / field2Value : 0
                break
              case "multiply":
                formulaResult = field1Value * field2Value
                break
              case "add":
                formulaResult = field1Value + field2Value
                break
              case "subtract":
                formulaResult = field1Value - field2Value
                break
              default:
                formulaResult = 0
            }

            // Format the result based on formula format
            if (formula.format === "percentage") {
              formulaResult = formulaResult * 100
            }

            row.rowData[formulaKey] = formulaResult
          })
        }
      })

      // Calculate row totals
      valueFields.forEach((valueField) => {
        const rowTotal = data
          .filter((dataRow) => {
            return rowFields.every((field) => {
              const rowValue = rowData[field]
              const dataValue = dataRow[field]
              return String(rowValue || "").trim() === String(dataValue || "").trim()
            })
          })
          .reduce((total, dataRow) => {
            const value = Number.parseFloat(dataRow[valueField]) || 0
            return total + value
          }, 0)

        row.totals[valueField] = rowTotal
      })

      // Calculate formula totals
      if (formulaColumns && formulaColumns.length > 0) {
        formulaColumns.forEach((formula) => {
          const getFieldTotal = (f) => {
            if (rowFields.includes(f)) return rowData[f] || 0
            return row.totals[f] || 0
          }

          const field1Total = getFieldTotal(formula.fields[0])
          const field2Total = getFieldTotal(formula.fields[1])

          let formulaResult = 0
          switch (formula.operation) {
            case "divide":
              formulaResult = field2Total !== 0 ? field1Total / field2Total : 0
              break
            case "multiply":
              formulaResult = field1Total * field2Total
              break
            case "add":
              formulaResult = field1Total + field2Total
              break
            case "subtract":
              formulaResult = field1Total - field2Total
              break
            default:
              formulaResult = 0
          }

          if (formula.format === "percentage") {
            formulaResult = formulaResult * 100
          }

          row.totals[formula.name] = formulaResult
        })
      }

      rows.push(row)
    })

    return rows
  }

  const calculateGrandTotals = (data, columnValues, columnFields, valueFields, pivotConfig, rows) => {
    const grandTotals = {}

    // Grand total for each column (product category)
    columnValues.forEach((columnValue) => {
      valueFields.forEach((valueField) => {
        const cellKey = `${columnValue}|${valueField}`

        const filteredData = data.filter((dataRow) =>
          columnFields.some((field) => {
            const dataValue = dataRow[field]
            return String(dataValue || "").trim() === String(columnValue || "").trim()
          }),
        )

        const sum = filteredData.reduce((total, dataRow) => {
          const value = Number.parseFloat(dataRow[valueField]) || 0
          return total + value
        }, 0)

        grandTotals[cellKey] = sum
      })

      // Calculate formula grand totals
      if (pivotConfig.formulaColumns && pivotConfig.formulaColumns.length > 0) {
        pivotConfig.formulaColumns.forEach((formula) => {
          const formulaKey = `${columnValue}|${formula.name}`

          const field1Total = grandTotals[`${columnValue}|${formula.fields[0]}`] || 0
          const field2Total = grandTotals[`${columnValue}|${formula.fields[1]}`] || 0

          let formulaResult = 0
          switch (formula.operation) {
            case "divide":
              formulaResult = field2Total !== 0 ? field1Total / field2Total : 0
              break
            case "multiply":
              formulaResult = field1Total * field2Total
              break
            case "add":
              formulaResult = field1Total + field2Total
              break
            case "subtract":
              formulaResult = field1Total - field2Total
              break
            default:
              formulaResult = 0
          }

          if (formula.format === "percentage") {
            formulaResult = formulaResult * 100
          }

          grandTotals[formulaKey] = formulaResult
        })
      }
    })

    // Overall grand total
    valueFields.forEach((valueField) => {
      const overallTotal = data.reduce((total, dataRow) => {
        const value = Number.parseFloat(dataRow[valueField]) || 0
        return total + value
      }, 0)

      grandTotals[`total_${valueField}`] = overallTotal
    })

    // Overall grand totals for formulas
    if (pivotConfig.formulaColumns && pivotConfig.formulaColumns.length > 0) {
      pivotConfig.formulaColumns.forEach((formula) => {
        // For formula grand totals, it's often better to sum the results from each row
        // especially if it's a multiplication of a row field (like price) and a value field (like qty)
        const isDivision = formula.operation === "divide"

        if (isDivision) {
          const field1Total = grandTotals[`total_${formula.fields[0]}`] || 0
          const field2Total = grandTotals[`total_${formula.fields[1]}`] || 0
          let formulaResult = field2Total !== 0 ? field1Total / field2Total : 0
          if (formula.format === "percentage") formulaResult = formulaResult * 100
          grandTotals[`total_${formula.name}`] = formulaResult
        } else {
          // Sum the formula results from each row for multiply, add, subtract
          const overallSum = rows.reduce((sum, row) => sum + (row.totals[formula.name] || 0), 0)
          grandTotals[`total_${formula.name}`] = overallSum
        }
      })
    }

    return grandTotals
  }

  const formatValue = (value, field, isFormula = false, formulaFormat = null) => {
    if (value === null || value === undefined || value === "" || value === 0) return ""

    if (isFormula && formulaFormat === "percentage") {
      return `${(value || 0).toFixed(3)}%`
    }

    const columnConfig = columnsData.find((col) => col.dataIndex === field)
    if (columnConfig?.type === "Numeric" || columnConfig?.type === "Amount") {
      return typeof value === "number" ? value.toLocaleString("en-US") : value
    }

    return value.toFixed(2)
  }

  const getDisplayName = (field) => {
    const columnConfig = columnsData.find((col) => col.dataIndex === field)
    return columnConfig?.title?.props?.children || field
  }

  if (!pivotConfig || pivotConfig.enabled !== "Y") {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Pivot configuration not enabled</p>
      </div>
    )
  }

  if (!gridData || gridData.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>No data available for pivot table</p>
      </div>
    )
  }

  const columnValues =
    pivotData.rows.length > 0
      ? Array.from(
        new Set(
          gridData
            .map((row) => pivotConfig.columns.map((col) => row[col]).join("|"))
            .filter((val) => val && val !== ""),
        ),
      ).sort()
      : []

  return (
    <div style={{ fontFamily: "Inter", backgroundColor: "#ffffff" }}>
      <style>
        {`
          .pivot-table-container {
            overflow: auto;
            max-height: 70vh;
            border: 1px solid #d9d9d9;
          }
          
          .pivot-table {
            border-collapse: collapse;
            font-family: "Inter";
            font-size: 12px;
            width: 100%;
            min-width: fit-content;
            background-color: #ffffff;
          }
          
          .pivot-table th {
            background-color: #f5f5f5;
            border: 1px solid #d9d9d9;
            font-weight: 600;
            font-family: "Inter";
            font-size: 10px;
            color: #000000;
            padding: 4px 8px;
            white-space: nowrap;
            text-align: center;
            position: sticky;
            top: 0;
            z-index: 10;
          }
          
          .pivot-table .row-header {
            background-color: #fafafa;
            font-weight: 600;
            text-align: left;
            position: sticky;
            left: 0;
            z-index: 5;
          }
          
          .pivot-table td {
            border: 1px solid #d9d9d9;
            padding: 4px 8px;
            font-family: "Inter";
            font-size: 10px;
            color: #000000;
            white-space: nowrap;
            text-align: right;
          }
          
          .pivot-table .row-data {
            text-align: left;
            background-color: #fafafa;
            font-weight: 500;
            position: sticky;
            left: 0;
            z-index: 5;
          }
          
          .pivot-table .total-row {
            font-weight: 600;
          }
          
          .pivot-table .grand-total-row {
            font-weight: 700;
          }
          
          .pivot-table tbody tr:hover td:not(.row-data) {
            background-color: #f0f0f0;
          }
          
          .pivot-table .numeric-cell {
            text-align: right;
          }
          
          .pivot-table .text-cell {
            text-align: left;
          }

          .pivot-table .formula-cell {
            background-color: #f9f9f9;
            font-style: italic;
          }
        `}
      </style>

      <Card>
        <Spin indicator={<LoadingOutlined style={{ fontSize: "52px" }} spin />} spinning={loading}>
          <div className="pivot-table-container">
            <table className="pivot-table">
              <thead>
                {/* Main header row */}
                <tr>
                  {/* Row field headers */}
                  {pivotConfig.rows.map((field) => (
                    <th key={field} style={{ textAlign: "center" }}>
                      {getDisplayName(field)}
                    </th>
                  ))}

                  {/* Column headers grouped by column values */}
                  {columnValues.map((columnValue) => {
                    const displayValue = columnValue.split("|").join(" - ")
                    const totalColumns = pivotConfig.values.length
                    return (
                      <th key={columnValue} colSpan={totalColumns} style={{ textAlign: "center" }}>
                        {displayValue}
                      </th>
                    )
                  })}

                  {/* Total column */}
                  <th
                    colSpan={
                      pivotConfig.values.length + (pivotConfig.formulaColumns ? pivotConfig.formulaColumns.length : 0)
                    }
                  >
                    Total
                  </th>
                </tr>

                {/* Sub-header row for individual columns */}
                <tr style={{ display: pivotConfig.formulaColumns && pivotConfig.formulaColumns.length > 0 ? "" : "none" }}>

                  {pivotConfig.rows.map((field) => (
                    <th key={`sub-${field}`} style={{ textAlign: "center" }}>

                    </th>
                  ))}


                  {columnValues.map((columnValue) => {
                    const subHeaders = []
                    pivotConfig.values.forEach((valueField) => {
                      subHeaders.push(
                        <th key={`${columnValue}-${valueField}`} style={{ textAlign: "center", fontSize: "9px" }}>
                          {getDisplayName(valueField)}
                        </th>,
                      )
                    })

                    return subHeaders
                  })}


                  {pivotConfig.values.map((valueField) => (
                    <th key={`total-sub-${valueField}`} style={{ textAlign: "center", fontSize: "9px" }}>
                      {getDisplayName(valueField)}
                    </th>
                  ))}

                  {pivotConfig.formulaColumns &&
                    pivotConfig.formulaColumns.map((formula) => (
                      <th key={`total-sub-${formula.name}`} style={{ textAlign: "center", fontSize: "9px" }}>
                        {formula.displayName}
                      </th>
                    ))}
                </tr>
              </thead>

              <tbody>
                {/* Data rows */}
                {pivotData.rows.map((row, rowIndex) => (
                  <tr key={row.key}>
                    {/* Row data */}
                    {pivotConfig.rows.map((field) => (
                      <td key={field} className="row-data">
                        {row.rowData[field]}
                      </td>
                    ))}

                    {/* Value cells for each column */}
                    {columnValues.map((columnValue) => {
                      const cells = []

                      // Add regular value fields
                      pivotConfig.values.forEach((valueField) => {
                        const cellKey = `${columnValue}|${valueField}`
                        const value = row.rowData[cellKey]
                        cells.push(
                          <td key={cellKey} className="numeric-cell">
                            {formatValue(value, valueField)}
                          </td>,
                        )
                      })

                      return cells
                    })}

                    {/* Row totals */}
                    {pivotConfig.values.map((valueField) => (
                      <td key={`total-${valueField}`} className="numeric-cell">

                        {formatValue(row.totals[valueField], valueField)}
                      </td>
                    ))}

                    {/* Formula row totals */}
                    {pivotConfig.formulaColumns &&
                      pivotConfig.formulaColumns.map((formula) => (
                        <td key={`total-${formula.name}`} className="numeric-cell formula-cell">
                          {formatValue(row.totals[formula.name], formula.name, true, formula.format)}
                        </td>
                      ))}
                  </tr>
                ))}

                {/* Grand total row */}
                <tr className="grand-total-row">
                  <td colSpan={pivotConfig.rows.length} className="row-data">
                    <strong>Grand Total</strong>
                  </td>

                  {/* Grand totals for each column */}
                  {columnValues.map((columnValue) => {
                    const cells = []

                    // Add regular value field totals
                    pivotConfig.values.forEach((valueField) => {
                      const cellKey = `${columnValue}|${valueField}`
                      const value = pivotData.grandTotals[cellKey]
                      cells.push(
                        <td key={cellKey} className="numeric-cell">
                          <strong>{formatValue(value, valueField)}</strong>
                        </td>,
                      )
                    })

                    return cells
                  })}

                  {/* Overall grand totals */}
                  {pivotConfig.values.map((valueField) => {
                    const value = pivotData.grandTotals[`total_${valueField}`]
                    return (
                      <td key={`grand-total-${valueField}`} className="numeric-cell">
                        <strong>{formatValue(value, valueField)}</strong>
                      </td>
                    )
                  })}

                  {/* Overall formula grand totals */}
                  {pivotConfig.formulaColumns &&
                    pivotConfig.formulaColumns.map((formula) => {
                      const value = pivotData.grandTotals[`total_${formula.name}`]
                      return (
                        <td key={`grand-total-${formula.name}`} className="numeric-cell formula-cell">
                          <strong>{formatValue(value, formula.name, true, formula.format)}</strong>
                        </td>
                      )
                    })}
                </tr>
              </tbody>
            </table>
          </div>
        </Spin>
      </Card>
    </div>
  )
}

export default PivotTableForAnalytics
